import React from "react";
import { Eye, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { TARGET_LABEL } from "./ImportMapping";
import { translateFr as tfr } from "@/i18n";

/**
 * ImportPreview — Step 4.
 * Shows a clean preview of the first ~10 cleaned player rows after mapping is applied.
 * Highlights duplicate rows. Read-only.
 */
export default function ImportPreview({ rows = [], mapping = {}, duplicates = [], totalRows = 0 }) {
  const dupSet = new Set(duplicates.map((d) => d.index));

  // Determine which target columns are mapped (and thus visible)
  const targetCols = ["name", "position", "club", "age", "level", "phone", "email"];
  const mappedTargets = targetCols.filter((t) =>
    Object.values(mapping).includes(t)
  );

  const colFor = (target) => Object.keys(mapping).find((k) => mapping[k] === target);

  const sample = rows.slice(0, 10);

  return (
    <div className="space-y-4" data-testid="import-step-preview">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-xl bg-info/10 border border-info/30 text-info flex items-center justify-center">
            <Eye className="h-4 w-4" />
          </div>
          <div>
            <div className="text-sm font-semibold">{tfr("Aperçu des joueurs")}</div>
            <div className="text-[11px] text-muted-foreground">
              {totalRows} {totalRows > 1 ? tfr("joueurs prêts à être importés") : tfr("joueur prêt à être importé")}
            </div>
          </div>
        </div>
        <Badge className="bg-success/10 text-success border-success/30 gap-1.5">
          <Sparkles className="h-3 w-3" /> Données nettoyées
        </Badge>
      </div>

      <div className="rounded-2xl border border-border dark:border-border bg-card dark:bg-surface/40 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-secondary/60 dark:bg-secondary/30 text-[10px] uppercase tracking-wider text-muted-foreground dark:text-muted-foreground/80">
              <tr>
                {mappedTargets.map((t) => (
                  <th key={t} className="text-left px-4 py-2.5 font-semibold whitespace-nowrap">
                    {TARGET_LABEL[t] || t}
                  </th>
                ))}
                <th className="text-right px-4 py-2.5 font-semibold whitespace-nowrap">{tfr("Statut")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/70 dark:divide-border/60">
              {sample.map((row, idx) => {
                const isDup = dupSet.has(idx);
                const nameCol = colFor("name");
                const rowKey = `${idx}-${(nameCol && row[nameCol]) || ""}`;
                return (
                  <tr
                    key={rowKey}
                    className={cn(
                      "transition-colors hover:bg-accent/40 dark:hover:bg-secondary/50 dark:bg-secondary/30",
                      isDup && "bg-warning/[0.04]"
                    )}
                    data-testid={`preview-row-${idx}`}
                  >
                    {mappedTargets.map((t) => {
                      const col = colFor(t);
                      const v = col ? row[col] : "";
                      const isName = t === "name";
                      return (
                        <td
                          key={t}
                          className={cn(
                            "px-4 py-2.5 truncate max-w-[220px]",
                            isName && "font-medium text-foreground",
                            !isName && "text-muted-foreground/90"
                          )}
                        >
                          {String(v ?? "") || <span className="text-muted-foreground/40">—</span>}
                        </td>
                      );
                    })}
                    <td className="px-4 py-2.5 text-right">
                      {isDup ? (
                        <Badge className="text-[10px] bg-warning/10 text-warning border-warning/30">Doublon</Badge>
                      ) : (
                        <Badge className="text-[10px] bg-success/10 text-success border-success/30">{tfr("Nouveau")}</Badge>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {totalRows > sample.length && (
          <div className="px-4 py-2.5 text-[11px] text-muted-foreground text-center bg-secondary/50 dark:bg-secondary/30 border-t border-border/70 dark:border-border">
            + {totalRows - sample.length} {totalRows - sample.length > 1 ? tfr("autres joueurs non affichés") : tfr("autre joueur non affiché")}
          </div>
        )}
      </div>
    </div>
  );
}
